import is from '@sindresorhus/is';
import { first } from 'lodash';
import type { ReactNode } from 'react';
import { useContext, useMemo } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';
import type { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import { DraftBadge } from '../components/DraftBadge';
import { PublishedBadge } from '../components/PublishedBadge';
import { ReviewedBadge } from '../components/ReviewedBadge';

const formatName = (version: JobProfileVersion): string => {
    if (!is.nullOrUndefined(version.createdBy)) {
        return `${version.createdBy?.firstName} ${version.createdBy?.lastName}`;
    }

    if (is.nullOrUndefined(version.reviewer)) {
        return '';
    }

    return `${version.reviewer?.firstName} ${version.reviewer?.lastName}`;
};

export const useFormattedVersions = (
    versions: readonly JobProfileVersion[],
): {
    readonly formattedVersions: readonly VersionSelect[];
    readonly newTmpVersion: VersionSelect;
} => {
    const { translate } = useContext(ApplicationContext);

    const newTmpVersion: VersionSelect = {
        id: ((first(versions?.map((version) => version?.version)) ?? 0) + 1).toString(),
        author: 'New Version',
        badge: <DraftBadge />,
        title: translate('pages.job-architecture-v2.job-profile-version', {
            version: ((first(versions)?.version ?? 0) + 1).toString(),
        }),
    };

    return {
        formattedVersions: useMemo(() => {
            const getBadge = (version: JobProfileVersion): ReactNode => {
                if (version.publishedAt !== null) {
                    return <PublishedBadge />;
                }

                if (version.reviewedAt !== null) {
                    return <ReviewedBadge />;
                }

                return <DraftBadge />;
            };

            return versions.map((version) => ({
                id: version.version.toString(),
                author: formatName(version),
                badge: getBadge(version),
                title: translate('pages.job-architecture-v2.job-profile-version', {
                    version: version.version.toString(),
                }),
            }));
        }, [versions, translate]),
        newTmpVersion,
    };
};
